import {
  AddToShoppingCartContext,
  ISearchProduct,
  IShoppingCartProductBundleInfo,
  TrackCartEvent,
} from '@monofront/types';
import { trackEvent } from '@monofront/tracking';
import { getHasHiddenPrice } from '@monofront/business/utils';
import { useAuthentication } from '@context/businessContainer';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import useI18n from '@hooks/i18n/useI18n';
import logger from '@utils/logger';
import { toast } from 'react-toastify';
import { useTranslations } from 'next-intl';
import { isMobileApp } from '@utils/environment';
import { EVENT_SOURCE } from '@monofront/business/constants';

interface IUseProductBasketToShoppingCartReturn {
  currentProductQuantity: number;
  editShoppingCart: (oldCount: number, newCount: number) => Promise<void>;
  hasProductsNotAvailable: boolean;
  shouldHidePrice: boolean;
  globalPrice: {
    price: number;
    comparedAtPrice: number;
  };
}

const useProductBasketToShoppingCart = (
  products: ISearchProduct[],
  bundleInfos: IShoppingCartProductBundleInfo,
  containerContext: AddToShoppingCartContext,
): IUseProductBasketToShoppingCartReturn => {
  const tGlobal = useTranslations('Global');
  const {
    shoppingCart,
    shoppingCartId,
    addProductsToShoppingCart,
    deleteProductsFromShoppingCart,
  } = useShoppingCart();
  const { isAuthenticated } = useAuthentication();
  const { currency } = useI18n();
  const productsBasketIds = products?.map((product) => product.id);

  const hasProductsNotAvailable = products.some(
    (product) => !product.inventory_available,
  );

  const getPrice = (field: string) => {
    const price = Number(
      products?.reduce((accumulator, item) => {
        if (!item.inventory_available) {
          return accumulator;
        }

        return accumulator + item[field];
      }, 0),
    );

    return price;
  };

  const globalPrice = {
    price: getPrice('price'),
    comparedAtPrice: getPrice('compare_at_price'),
  };

  const formattedProductsAvailable = products.filter((product) =>
    Boolean(product.inventory_available),
  );

  const productsBasketInShoppingCart = shoppingCart?.products?.filter(
    (product) => {
      return productsBasketIds.includes(product.id);
    },
  );

  const currentProductQuantity =
    (productsBasketInShoppingCart &&
      productsBasketInShoppingCart.length >=
        formattedProductsAvailable.length &&
      Math.min(
        ...productsBasketInShoppingCart.map((product) => product.quantity),
      )) ||
    0;

  const shouldHidePrice = Boolean(
    !isAuthenticated &&
      products.some((product) => {
        return getHasHiddenPrice(product);
      }),
  );

  const editShoppingCart = async (oldCount: number, newCount: number) => {
    if (!shoppingCartId) {
      logger.error(new Error('Unexpected empty `shoppingCartId`'));
      toast(tGlobal('errorOccurredTryAgain'), {
        progressClassName: 'toast-error',
      });

      return;
    }

    const bundleTrackingData: TrackCartEvent['data'] = {
      products: [
        {
          sku: bundleInfos.uid,
          name: bundleInfos.name,
          brand: '',
          category: bundleInfos.category,
          price: Number((globalPrice.price * 100).toFixed()),
          quantity: 1,
        },
      ],
      context: containerContext,
      currency,
      is_substitute: false,
      eventSource: isMobileApp ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
    };

    if (newCount === 0) {
      await deleteProductsFromShoppingCart(
        shoppingCartId,
        formattedProductsAvailable,
      );

      trackEvent({
        type: 'LFRemoveFromCart',
        data: bundleTrackingData,
      });

      return;
    }

    const productsToAdd = formattedProductsAvailable.map((product) => {
      const quantityShoppingCart =
        productsBasketInShoppingCart?.find(
          (shoppingCartProduct) =>
            Number(shoppingCartProduct.id) === Number(product.id),
        )?.quantity || 0;

      const newQuantity =
        quantityShoppingCart && oldCount > newCount
          ? quantityShoppingCart - 1
          : quantityShoppingCart + 1;

      return {
        ...product,
        quantity: newQuantity,
      };
    });

    await addProductsToShoppingCart(shoppingCartId, productsToAdd);

    trackEvent({
      type: 'LFAddToCart',
      data: bundleTrackingData,
    });
  };

  return {
    currentProductQuantity,
    editShoppingCart,
    hasProductsNotAvailable,
    shouldHidePrice,
    globalPrice,
  };
};

export default useProductBasketToShoppingCart;
